
// src/pages/SiteDetailPage.jsx
//
// Module 04 — Site detail / key management
// All key actions go through confirmation modals; the secret reveal modal
// shows the plaintext exactly once with copy-to-clipboard, then drops it.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getSite,
  updateSite,
  deleteSite,
  rotateKey,
  generateSecret,
  disableSecret,
} from '../api/sitesApi';
import RequirePermission from '../auth/RequirePermission'
export default function SiteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 'edit' | 'delete' | 'rotate' | 'secret' | 'disable-secret' | null
  const [modal, setModal] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSite(id);
      setSite(data);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const closeModal = () => setModal(null);
  const closeAndReload = () => { setModal(null); reload(); };

  if (loading) {
    return <div className="shell"><div className="card">Loading…</div></div>;
  }

  if (error || !site) {
    return (
      <div className="shell">
        <button className="ghost back-link" onClick={() => navigate('/sites')}>← Sites</button>
        <div className="card toast danger">{error || 'Site not found.'}</div>
      </div>
    );
  }

  return (
    <div className="shell">
      <header className="page-header">
        <div>
          <button className="ghost back-link" onClick={() => navigate('/sites')}>
            ← Sites
          </button>
          <h1>{site.name}</h1>
          <p className="muted detail-meta">
            <a href={`https://${site.domain}`} target="_blank" rel="noopener noreferrer">
              {site.domain}
            </a>
            <span>·</span>
            <StatusBadge status={site.status} />
            <span>·</span>
            <span>Owner: {site.ownerEmail || '—'}</span>
          </p>
        </div>
        <div className="actions">
          <RequirePermission code="sites.update">
            <button className="ghost" onClick={() => setModal('edit')}>Edit</button>
          </RequirePermission>
          <RequirePermission code="sites.delete">
            <button className="ghost danger" onClick={() => setModal('delete')}>Delete</button>
          </RequirePermission>
        </div>
      </header>

      {/* Active key panel */}
      <section className="card">
        <header className="card-header">
          <h2>Active key</h2>
          <RequirePermission code="sites.manage_keys">
            <button className="ghost" onClick={() => setModal('rotate')}>Rotate</button>
          </RequirePermission>
        </header>
        {site.activeKey ? (
          <ActiveKeyView
            siteKey={site.activeKey}
            onGenerateClick={() => setModal('secret')}
            onDisableClick={() => setModal('disable-secret')}
          />
        ) : (
          <p className="muted">No active key. Click <strong>Rotate</strong> to issue one.</p>
        )}
      </section>

      {/* Key history */}
      {site.keyHistory && site.keyHistory.length > 0 && (
        <section className="card">
          <h2>Key history</h2>
          <p className="muted">Previously active keys, retained for audit.</p>
          <table className="table">
            <thead>
              <tr>
                <th>Public key</th>
                <th>Created</th>
                <th>Revoked</th>
                <th>Signing was</th>
              </tr>
            </thead>
            <tbody>
              {site.keyHistory.map((k) => (
                <tr key={k.id}>
                  <td><code className="key-history-code">{k.publicKey}</code></td>
                  <td>{formatDateTime(k.createdAt)}</td>
                  <td>{formatDateTime(k.revokedAt)}</td>
                  <td>{k.hasSecret ? 'Enabled' : 'Off'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Modals */}
      {modal === 'edit' && (
        <EditSiteModal site={site} onClose={closeModal} onSaved={closeAndReload} />
      )}
      {modal === 'delete' && (
        <DeleteSiteModal
          site={site}
          onClose={closeModal}
          onDeleted={() => navigate('/sites')}
        />
      )}
      {modal === 'rotate' && (
        <RotateKeyModal site={site} onClose={closeModal} onRotated={closeAndReload} />
      )}
      {modal === 'secret' && (
        <SecretActionModal site={site} onClose={closeModal} onDone={closeAndReload} />
      )}
      {modal === 'disable-secret' && (
        <DisableSecretModal site={site} onClose={closeModal} onDisabled={closeAndReload} />
      )}
    </div>
  );
}

// ── Active key view ───────────────────────────────────────────────────

function ActiveKeyView({ siteKey, onGenerateClick, onDisableClick }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(siteKey.publicKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  }

  return (
    <div className="key-detail">
      <div className="key-row">
        <label>Public key</label>
        <div className="key-value">
          <code>{siteKey.publicKey}</code>
          <button className="ghost" onClick={copy}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        <small className="muted">Safe to expose in your SDK / tracking script.</small>
      </div>

      <div className="key-row">
        <label>Signing secret</label>
        {siteKey.hasSecret ? (
          <>
            <div className="key-value">
              <code>•••• {siteKey.secretLastFour}</code>
              <span className="badge">Enabled</span>
            </div>
            <small className="muted">
              SDK signs requests with HMAC. The plaintext was shown once at generation.
            </small>
            <div className="actions inline-actions">
              <RequirePermission code="sites.manage_keys">
                <button className="ghost" onClick={onGenerateClick}>Regenerate</button>
                <button className="ghost danger" onClick={onDisableClick}>Disable signing</button>
              </RequirePermission>
            </div>
          </>
        ) : (
          <>
            <div className="key-value">
              <span className="muted">Signing not enabled — public key only.</span>
            </div>
            <div className="actions inline-actions">
              <RequirePermission code="sites.manage_keys">
                <button className="primary" onClick={onGenerateClick}>
                  Generate secret
                </button>
              </RequirePermission>
            </div>
          </>
        )}
      </div>

      <div className="key-meta muted">
        Created {formatDateTime(siteKey.createdAt)}
      </div>
    </div>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────

function EditSiteModal({ site, onClose, onSaved }) {
  const [name, setName] = useState(site.name);
  const [domain, setDomain] = useState(site.domain);
  const [status, setStatus] = useState(site.status);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await updateSite(site.id, { name: name.trim(), domain: domain.trim(), status });
      onSaved();
    } catch (e2) {
      setErr(extractError(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card" onClick={(e) => e.stopPropagation()}>
        <h2>Edit site</h2>
        <form onSubmit={submit}>
          <label>
            Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={128}
              required
              autoFocus
              disabled={busy}
            />
          </label>
          <label>
            Domain
            <input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              maxLength={253}
              required
              disabled={busy}
            />
            <small className="muted">Will be re-normalized server-side.</small>
          </label>
          <label>
            Status
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={busy}
            >
              <option value="Active">Active</option>
              <option value="Disabled">Disabled — events from this site will be rejected</option>
            </select>
          </label>
          {err && <div className="toast danger">{err}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Delete modal ──────────────────────────────────────────────────────

function DeleteSiteModal({ site, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function confirm() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteSite(site.id);
      onDeleted();
    } catch (e) {
      setErr(extractError(e));
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card" onClick={(e) => e.stopPropagation()}>
        <h2>Delete site?</h2>
        <p>
          This permanently deletes <strong>{site.name}</strong>{' '}
          (<code>{site.domain}</code>) along with all tracking keys.
          Events from this site will be rejected going forward.
          This cannot be undone.
        </p>
        {err && <div className="toast danger">{err}</div>}
        <div className="actions">
          <button className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="danger" onClick={confirm} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete site'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Rotate key modal ──────────────────────────────────────────────────

function RotateKeyModal({ site, onClose, onRotated }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const hadSecret = !!site.activeKey?.hasSecret;

  async function confirm() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await rotateKey(site.id);
      onRotated();
    } catch (e) {
      setErr(extractError(e));
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card" onClick={(e) => e.stopPropagation()}>
        <h2>Rotate public key?</h2>
        <p>
          Issues a fresh public key and immediately revokes the current one.
          Tracking scripts still using the old key will stop reporting events
          until updated.
        </p>
        {hadSecret && (
          <div className="warning">
            ⚠️ Signing was enabled. The new key does <strong>not</strong> carry
            the secret over — you must call <strong>Generate secret</strong> again
            on the new key if you want signing to continue.
          </div>
        )}
        {err && <div className="toast danger">{err}</div>}
        <div className="actions">
          <button className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="danger" onClick={confirm} disabled={busy}>
            {busy ? 'Rotating…' : 'Rotate key'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Secret generate / regenerate (with one-time reveal) ───────────────

function SecretActionModal({ site, onClose, onDone }) {
  const wasRegen = !!site.activeKey?.hasSecret;
  const [stage, setStage] = useState('confirm'); // 'confirm' | 'revealed'
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [revealed, setRevealed] = useState(null);
  const [copied, setCopied] = useState(false);

  async function doGenerate() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const data = await generateSecret(site.id);
      setRevealed(data);
      setStage('revealed');
    } catch (e) {
      setErr(extractError(e));
    } finally {
      setBusy(false);
    }
  }

  async function copyAll() {
    try {
      await navigator.clipboard.writeText(revealed.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  }

  if (stage === 'confirm') {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="card" onClick={(e) => e.stopPropagation()}>
          <h2>{wasRegen ? 'Regenerate' : 'Generate'} signing secret</h2>
          {wasRegen ? (
            <p>
              This <strong>invalidates the current secret</strong>. Any clients
              signing requests with it will start failing until updated with the
              new one. The new secret will be shown <strong>once</strong>.
            </p>
          ) : (
            <p>
              Issues a new HMAC signing secret for the active key.
              The plaintext will be shown <strong>once</strong> — copy it now;
              you can't retrieve it later (only its hash is persisted).
            </p>
          )}
          {err && <div className="toast danger">{err}</div>}
          <div className="actions">
            <button className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="primary" onClick={doGenerate} disabled={busy}>
              {busy ? 'Generating…' : (wasRegen ? 'Regenerate' : 'Generate')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // revealed
  return (
    <div className="modal-backdrop" onClick={() => { /* block dismiss until acknowledged */ }}>
      <div className="card secret-reveal" onClick={(e) => e.stopPropagation()}>
        <h2>Your new secret</h2>
        <div className="warning">
          ⚠️ This is the <strong>only time</strong> the plaintext will be shown.
          Copy it now — it cannot be retrieved later.
        </div>
        <div className="secret-display">
          <code>{revealed.secret}</code>
          <button className="primary" onClick={copyAll}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        <p className="muted">
          Public key: <code>{revealed.publicKey}</code>
          <br />
          Last four (saved as display hint): <code>{revealed.secretLastFour}</code>
        </p>
        <div className="actions">
          <button className="primary" onClick={onDone}>
            I've saved it — close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Disable secret modal ──────────────────────────────────────────────

function DisableSecretModal({ site, onClose, onDisabled }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function confirm() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await disableSecret(site.id);
      onDisabled();
    } catch (e) {
      setErr(extractError(e));
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card" onClick={(e) => e.stopPropagation()}>
        <h2>Disable signing?</h2>
        <p>
          The active key continues to identify your site, but the SDK no longer
          needs to HMAC-sign requests. You can re-enable signing anytime by
          clicking <strong>Generate secret</strong>.
        </p>
        {err && <div className="toast danger">{err}</div>}
        <div className="actions">
          <button className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="danger" onClick={confirm} disabled={busy}>
            {busy ? 'Disabling…' : 'Disable signing'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Small inline components / helpers ────────────────────────────────

function StatusBadge({ status }) {
  const cls =
    (status || '').toLowerCase() === 'disabled' ? 'badge danger' : 'badge';
  return <span className={cls}>{status}</span>;
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function extractError(err) {
  const data = err?.response?.data;
  if (data?.message) return data.message;
  if (typeof err?.message === 'string') return err.message;
  return 'Something went wrong. Please try again.';
}